import { SpatialGradientDTO, MaxSARDTO } from '../../models/MRIMachineModels';
import { GradientAnalysisDTO } from '../../services/api/mriMachineCompatibilityService';
import { Category } from '../ui-components/category/categoryColors';
import {
    EquipmentDTO,
    LinkedFileDTO,
    LinkedPolicyDTO,
} from '../../services/apiService';

export interface PopulatedFile {
    _id: string;
    filename: string;
    documentType: string;
    url?: string;
    key?: string;
}

export interface MRIMachinePopulated {
    _id: string;
    isPublic?: boolean;
    Model: string;
    Manufacturer?: string;
    Name: string;
    FieldStrength: number;
    SpatialGradient: SpatialGradientDTO[];
    SlewRatePerAxis: number;
    MaxSlewRate: number;
    MaxSAR: MaxSARDTO[];
    MaxB1rms: string;
    TRCoils: string;
    attachedFiles?: PopulatedFile[];
    machineImage?: PopulatedFile;
    notes?: string;
    location?: string;
    category?: Category;
    equipment?: EquipmentDTO[];
    policies?: LinkedPolicyDTO[];
}

export interface MRIMachineFormData {
    _id?: string;
    Model: string;
    Manufacturer?: string;
    Name: string;
    FieldStrength: number;
    SpatialGradient: SpatialGradientDTO[];
    SlewRatePerAxis: number;
    MaxSlewRate: number;
    MaxSAR: MaxSARDTO[];
    MaxB1rms: string;
    TRCoils: string;
    attachedFiles?: LinkedFileDTO[];
    machineImage?: string;
    machineImageUrl?: string;
    notes?: string;
    location?: string;
    category?: Category;
    policies?: LinkedPolicyDTO[];
}

export const EQUIPMENT_TYPES = [
    'Patient monitor',
    'Coil',
    'Injector',
    'Anesthesia',
    'Ventilator',
    'Patient positioning',
    'Accessory',
] as const;

export interface EquipmentValues {
    name: string;
    type: string;
    notes: string;
    image: string | null;
    files: LinkedFileDTO[];
    policies: LinkedPolicyDTO[];
}

export const toEquipmentInput = ({
    files,
    policies,
    ...values
}: EquipmentValues) => ({
    ...values,
    files: files.map((f) => f._id),
    policies: policies.map((p) => p._id),
});

export interface EquipmentDraft extends EquipmentValues {
    id: string;
}

export type NumericInput = number | '';

export interface MachineFormValues {
    Name: string;
    Model: string;
    Manufacturer: string;
    FieldStrength: number;
    SpatialGradient: { id: string; diameter: string; value: NumericInput }[];
    SlewRatePerAxis: NumericInput;
    MaxSlewRate: NumericInput;
    MaxSAR: {
        id: string;
        level: NumericInput;
        wholeBody: NumericInput;
        head: NumericInput;
    }[];
    MaxB1rms: string;
    TRCoils: string;
    attachedFiles: LinkedFileDTO[];
    machineImage: string;
    notes: string;
    location: string;
    category: Category | null;
    equipment: EquipmentDraft[];
    policies: LinkedPolicyDTO[];
}

export interface MachineFavorite {
    machine: MRIMachinePopulated;
}

export const NO_LOCATION = '__no_location__';
export const NO_CATEGORY = '__no_category__';

export interface MachineFilterOptions {
    fieldStrengths: number[];
    models: string[];
    maxSpatialGradient?: number;
    coilTypes?: string[];
    locations: string[];
    categories: Category[];
}

export interface MachineFilterState {
    fieldStrength?: number[];
    model?: string[];
    maxSpatialGradient?: number;
    coilType?: string[];
    favoritesOnly?: boolean;
    location?: string;
    category?: string;
}

export interface CompatibilityAnnotations {
    gradientAnalysis?: GradientAnalysisDTO | null;
    isRestricted?: boolean;
    coilWarning?: string | null;
}
